import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Loader2, ShieldAlert, Crown, BellRing, Check, Mail, Sparkles, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { type BillingPlanView, getAnnualSavingsBadgeLabel, getBillingPlanAmount } from '@/lib/billingPlans';
import { getFunctionInvokeDisplayMessage } from '@/lib/functionInvokeErrors';
import { useToast } from '@/hooks/use-toast';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { useSupportContact } from '@/hooks/useSupportContact';
import { buildSupportMailto } from '@/lib/supportContact';
import { markCheckoutRecoveryPending, shouldResumeCheckoutRecovery } from '@/lib/checkoutRecovery';
import { trackAmplitudeEvent } from '@/lib/amplitudeAnalytics';
import { signOutLocally } from '@/lib/auth';
import { SupplierClaimOfferSlot } from '@/components/onboarding/SupplierClaimOfferSlot';
import { useSupplierClaimDecisionGate } from '@/hooks/useSupplierClaimDecisionGate';

/** ENT-6459: which mount point shows the paywall, recorded with the claim offer audit row. */
export type SubscriptionPaywallSurface = 'plan_selection' | 'app_gate';

/**
 * ENT-6459: "I'm a customer, choose a plan" hides the claim offer for the rest of the session.
 * The stored value is the user id, so another account signed in later in the same tab still
 * sees its own offer.
 */
export const SUPPLIER_CLAIM_OFFER_DISMISSED_STORAGE_KEY = 'sg.supplierClaimOffer.dismissed';

function readSupplierClaimOfferDismissed(userId: string | null): boolean {
  if (!userId) return false;
  try {
    return window.sessionStorage.getItem(SUPPLIER_CLAIM_OFFER_DISMISSED_STORAGE_KEY) === userId;
  } catch {
    return false;
  }
}

function writeSupplierClaimOfferDismissed(userId: string | null) {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(SUPPLIER_CLAIM_OFFER_DISMISSED_STORAGE_KEY, userId);
  } catch {
    // Storage can be unavailable (private mode); the card is still hidden for this render.
  }
}

interface SubscriptionGateProps {
  children: ReactNode;
  loadingFallback?: ReactNode;
  /** ENT-6459: the mount point, so the claim offer audit row names its surface. */
  paywallSurface: SubscriptionPaywallSurface;
}

export function SubscriptionGate({ children, loadingFallback, paywallSurface }: SubscriptionGateProps) {
  const { membership, isMspContext, isMspOrg, organization } = useOrganization();
  const {
    subscribed,
    isLoading,
    billingType,
    subscriptionStatus,
    subscriptionEnd,
    requestRenewal,
  } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRequestingRenewal, setIsRequestingRenewal] = useState(false);
  const [hasRequestedRenewal, setHasRequestedRenewal] = useState(false);
  const hasAccessUntilPeriodEnd =
    billingType === 'stripe' &&
    subscriptionStatus === 'canceled' &&
    Boolean(subscriptionEnd) &&
    new Date(subscriptionEnd).getTime() > Date.now();
  const hasPendingStripeCheckoutRecovery = shouldResumeCheckoutRecovery(
    organization?.id,
    billingType,
    subscriptionStatus,
  );

  useEffect(() => {
    if (!hasPendingStripeCheckoutRecovery) {
      return;
    }

    if (location.pathname === '/plan-selection' && location.search.includes('checkout=success')) {
      return;
    }

    navigate('/plan-selection?checkout=success', { replace: true });
  }, [hasPendingStripeCheckoutRecovery, location.pathname, location.search, navigate]);

  // Still loading subscription status
  if (isLoading) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // MSP surfaces are not gated by customer self-serve billing.
  // - `isMspContext`: viewing a managed customer through the MSP shell
  // - `isMspOrg`: operating in the MSP's own workspace
  if (isMspContext || isMspOrg) {
    return <>{children}</>;
  }

  // Subscribed (any billing type) — allow through
  if (subscribed || hasAccessUntilPeriodEnd) {
    return <>{children}</>;
  }

  if (hasPendingStripeCheckoutRecovery) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No subscription — show appropriate locked state
  const isAdmin = membership?.role === 'admin';

  // Manual (MSP-managed) billing expired — show contact message
  if (billingType === 'manual') {
    const handleRequestRenewal = async () => {
      setIsRequestingRenewal(true);

      try {
        const result = await requestRenewal();
        setHasRequestedRenewal(true);
        toast({
          title: result.status === 'already_requested' ? 'Renewal already requested' : 'Renewal requested',
          description:
            result.status === 'already_requested'
              ? "We've already notified the billing team and your provider."
              : "We've notified our billing team and your provider that you're ready to renew.",
        });
      } catch (error) {
        toast({
          title: 'Request failed',
          description: error instanceof Error ? error.message : 'Unable to request renewal right now.',
          variant: 'destructive',
        });
      } finally {
        setIsRequestingRenewal(false);
      }
    };

    return (
      <ManualBillingExpiredState
        hasRequestedRenewal={hasRequestedRenewal}
        isRequestingRenewal={isRequestingRenewal}
        onRequestRenewal={handleRequestRenewal}
      />
    );
  }

  // Standard users (non-admin) — can't manage billing
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-6">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Subscription Required</CardTitle>
            <CardDescription>
              Your organisation's subscription is inactive. Please contact your administrator to activate a plan.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <UseAnotherAccountButton />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin sees plan selection
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex items-start justify-center px-4 py-5 lg:px-5 lg:py-6">
      <SubscriptionPaywall surface={paywallSurface} />
    </div>
  );
}

function formatPlanPrice(pennies: number) {
  const value = pennies / 100;
  const isWhole = Number.isInteger(value);
  return `£${isWhole ? value.toLocaleString('en-GB') : value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ManualBillingExpiredState({
  hasRequestedRenewal,
  isRequestingRenewal,
  onRequestRenewal,
}: {
  hasRequestedRenewal: boolean;
  isRequestingRenewal: boolean;
  onRequestRenewal: () => Promise<void>;
}) {
  const { supportEmail } = useSupportContact();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-6">
      <Card className="max-w-md w-full text-center">
        <CardHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldAlert className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Subscription Expired</CardTitle>
          <CardDescription>
            Your organisation&apos;s subscription has expired. Request renewal and we&apos;ll notify the billing team and your managed service provider automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={onRequestRenewal} disabled={isRequestingRenewal || hasRequestedRenewal}>
            {isRequestingRenewal
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <BellRing className="h-4 w-4 mr-2" />}
            {hasRequestedRenewal ? 'Renewal requested' : 'Request renewal'}
          </Button>
          <Button variant="ghost" asChild>
            <a href={buildSupportMailto(supportEmail, 'Subscription Renewal')}>
              <Mail className="h-4 w-4 mr-2" />
              Contact Support
            </a>
          </Button>
          {hasRequestedRenewal && (
            <p className="text-sm text-muted-foreground">
              We&apos;ve notified our billing team and your provider that your organisation is ready to renew.
            </p>
          )}
          <div className="flex justify-center">
            <UseAnotherAccountButton />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type PaywallVariant = 'past_due' | 'canceled_post_grace' | 'never_subscribed';

function resolvePaywallVariant(
  billingType: string | null,
  subscriptionStatus: string | null,
  subscriptionEnd: string | null,
): PaywallVariant {
  // ENT-1297: distinguish "payment failed on an existing sub" from "post-grace
  // cancellation" from "never had a plan". The legacy heading was always
  // "Choose Your Plan" which is wrong for the first two — the user already
  // chose a plan, what they actually need is payment recovery or reactivation.
  if (billingType === 'stripe' && subscriptionStatus === 'past_due') {
    return 'past_due';
  }
  if (billingType === 'stripe' && subscriptionStatus === 'canceled') {
    const periodEnded = !subscriptionEnd || new Date(subscriptionEnd).getTime() <= Date.now();
    if (periodEnded) {
      return 'canceled_post_grace';
    }
  }
  return 'never_subscribed';
}

function SubscriptionPaywall({ surface }: { surface: SubscriptionPaywallSurface }) {
  const {
    startCheckout,
    refetch,
    billingType,
    subscriptionStatus,
    subscriptionEnd,
    planSlug,
    openBillingPortal,
  } = useSubscription();
  const { organization, user } = useOrganization();
  const { plans, isLoading: plansLoading, error: plansError, refetch: refetchPlans } = useSubscriptionPlans();
  const { toast } = useToast();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [pendingStripePlanChange, setPendingStripePlanChange] = useState<{
    targetPlanSlug: string;
    targetBillingInterval: 'monthly' | 'annual';
  } | null>(null);
  const billingInterval = isAnnual ? 'annual' : 'monthly';
  const variant = resolvePaywallVariant(billingType, subscriptionStatus, subscriptionEnd);
  // ENT-6459 (D2, fail-closed-on-unknown): a listed supplier who reached the customer paywall is
  // offered the free claim, and no plan can be chosen until the candidate is known.
  const supplierClaimGate = useSupplierClaimDecisionGate({ applies: variant === 'never_subscribed' });
  const userId = user?.id ?? null;
  const [supplierClaimOfferDismissed, setSupplierClaimOfferDismissed] = useState(
    () => readSupplierClaimOfferDismissed(userId),
  );
  const showSupplierClaimSlot = supplierClaimGate.state !== 'ready'
    || (supplierClaimGate.candidate !== null && !supplierClaimOfferDismissed);
  const handleDismissSupplierClaimOffer = () => {
    writeSupplierClaimOfferDismissed(userId);
    setSupplierClaimOfferDismissed(true);
  };

  useEffect(() => {
    const resetCheckoutState = () => {
      setLoadingPlan(null);
    };

    window.addEventListener('pageshow', resetCheckoutState);

    return () => {
      window.removeEventListener('pageshow', resetCheckoutState);
    };
  }, []);

  const getErrorMessage = (error: unknown) =>
    getFunctionInvokeDisplayMessage(error, 'Unable to start checkout right now');

  const isSupplierPlusCreditSelection = (targetPlan: BillingPlanView) =>
    billingType === 'stripe' &&
    planSlug === 'supplier_plus' &&
    targetPlan.planAudience === 'customer';

  const handleSelectPlan = async (slug: string) => {
    // ENT-6459: never start checkout while the supplier claim candidate is unknown.
    if (loadingPlan || supplierClaimGate.decisionsBlocked) {
      return;
    }

    setLoadingPlan(slug);
    let shouldResetLoadingPlan = true;

    try {
      // ENT-995/996: when the org already has an active Stripe subscription
      // (e.g. a SupplierPlus subscriber whose customer-gate is closed by
      // ENT-973's plan_audience check, or any subscriber switching plans
      // from the paywall), route to Stripe Customer Portal's
      // subscription_update_confirm flow. Stripe shows proration credit
      // natively. Without this branch create-checkout would mint a SECOND
      // concurrent Stripe sub on the same customer.
      const hasExistingStripeSub =
        billingType === 'stripe' &&
        subscriptionStatus != null &&
        ['active', 'past_due', 'trialing'].includes(subscriptionStatus);
      const targetPlan = plans.find((plan) => plan.slug === slug) ?? null;

      if (hasExistingStripeSub) {
        if (targetPlan && isSupplierPlusCreditSelection(targetPlan)) {
          setPendingStripePlanChange({
            targetPlanSlug: slug,
            targetBillingInterval: billingInterval,
          });
          return;
        }
        // ENT-995/996 + Codex P1: mark checkout-recovery pending BEFORE
        // routing to Stripe Portal so a webhook lag after the portal
        // confirmation doesn't strand the user behind the SubscriptionGate.
        // shouldResumeCheckoutRecovery picks this up on return and replays
        // the gate's existing recovery flow (redirect to /plan-selection?
        // checkout=success → BillingSettings sync effect).
        if (organization?.id) {
          markCheckoutRecoveryPending(organization.id);
        }
        await openBillingPortal({
          flowType: 'change_plan',
          targetPlanSlug: slug,
          targetBillingInterval: billingInterval,
        });
        shouldResetLoadingPlan = false;
      } else {
        trackAmplitudeEvent('Billing Checkout Started', {
          'account id': organization?.id ?? null,
          'plan selected': slug,
          'billing interval': billingInterval,
          'billing path': 'checkout',
          'checkout source': 'subscription_gate',
          'current plan slug': planSlug,
          'subscription status': subscriptionStatus,
        });
        await startCheckout(slug, billingInterval);
        shouldResetLoadingPlan = false;
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Checkout failed', description: message, variant: 'destructive' });
    } finally {
      if (shouldResetLoadingPlan) {
        setLoadingPlan((current) => (current === slug ? null : current));
      }
    }
  };

  const handleConfirmStripePlanChange = async () => {
    if (!pendingStripePlanChange) {
      return;
    }

    setLoadingPlan(pendingStripePlanChange.targetPlanSlug);
    try {
      if (organization?.id) {
        markCheckoutRecoveryPending(organization.id);
      }
      await openBillingPortal({
        flowType: 'change_plan',
        targetPlanSlug: pendingStripePlanChange.targetPlanSlug,
        targetBillingInterval: pendingStripePlanChange.targetBillingInterval,
      });
    } finally {
      setLoadingPlan(null);
      setPendingStripePlanChange(null);
    }
  };

  if (plansLoading) {
    return (
      <Card className="max-w-4xl w-full">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (plansError) {
    return (
      <Card className="max-w-4xl w-full">
        <CardHeader className="text-center">
          <CardTitle>Billing Plans Unavailable</CardTitle>
          <CardDescription>
            We could not load your billing plans right now. Please try again.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={() => refetchPlans()}>
            Retry
          </Button>
          <UseAnotherAccountButton />
        </CardContent>
      </Card>
    );
  }

  const annualSavingsBadgeLabel = getAnnualSavingsBadgeLabel(plans);

  return (
    <>
      <AlertDialog
        open={pendingStripePlanChange !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingStripePlanChange(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm your SupplierPlus upgrade</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This updates your existing Stripe subscription rather than starting a second subscription.
              </span>
              <span className="block">
                Any unused SupplierPlus time is credited by Stripe as proration during confirmation.
              </span>
              <span className="block">
                Stripe calculates the final amount when you confirm the plan change.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(loadingPlan)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(loadingPlan)}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmStripePlanChange();
              }}
            >
              Continue in Stripe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="w-full max-w-[42rem] border-0 bg-transparent shadow-none lg:max-w-[74rem]">
        <CardHeader className="px-3 pb-4 pt-4 text-center sm:px-4 xl:px-6">
          {showSupplierClaimSlot ? (
            <div className="mx-auto mb-4 w-full max-w-3xl text-left">
              <SupplierClaimOfferSlot
                gate={supplierClaimGate}
                showOffer={!supplierClaimOfferDismissed}
                surface={surface}
                currentOrganizationName={organization?.name ?? null}
                secondaryLabel="I'm a customer, choose a plan"
                onContinueAsCustomer={handleDismissSupplierClaimOffer}
              />
            </div>
          ) : null}
          <div className="flex items-center justify-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Crown className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-2xl" data-testid="subscription-paywall-heading">
              {variant === 'past_due'
                ? 'Update your payment method'
                : variant === 'canceled_post_grace'
                  ? 'Reactivate your subscription'
                  : 'Choose Your Plan'}
            </CardTitle>
          </div>
          <CardDescription>
            {variant === 'past_due'
              ? 'Your latest payment did not go through. Update your card in Stripe to restore access.'
              : variant === 'canceled_post_grace'
                ? 'Your subscription has ended. Pick a plan to reactivate.'
                : 'Select a subscription plan to access the platform'}
          </CardDescription>
          {variant !== 'past_due' && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Label className="text-sm text-muted-foreground">Monthly</Label>
              <Switch
                checked={isAnnual}
                disabled={Boolean(loadingPlan)}
                onCheckedChange={setIsAnnual}
              />
              <Label className="text-sm text-muted-foreground">
                Annual
                {annualSavingsBadgeLabel ? (
                  <Badge variant="secondary" className="ml-1 text-xs">{annualSavingsBadgeLabel}</Badge>
                ) : null}
              </Label>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4 px-3 pb-4 sm:px-4 xl:px-6">
          {variant === 'past_due' ? (
            <div className="flex flex-col items-center gap-3">
              <Button
                data-testid="subscription-paywall-update-payment"
                onClick={async () => {
                  if (isUpdatingPayment) return;
                  setIsUpdatingPayment(true);
                  try {
                    await openBillingPortal({ flowType: 'manage' });
                  } catch (error) {
                    toast({
                      title: 'Could not open billing portal',
                      description: getErrorMessage(error),
                      variant: 'destructive',
                    });
                  } finally {
                    setIsUpdatingPayment(false);
                  }
                }}
                disabled={isUpdatingPayment}
                className="min-w-48"
              >
                {isUpdatingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening Stripe…
                  </>
                ) : (
                  'Update payment method'
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                We&apos;ll open the Stripe Customer Portal so you can update your card or pay the outstanding invoice.
              </p>
            </div>
          ) : (
            <div data-testid={variant === 'canceled_post_grace' ? 'subscription-paywall-reactivate' : 'subscription-paywall-plan-grid'}>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.slug}
                    plan={plan}
                    isAnnual={isAnnual}
                    isSelected={loadingPlan === plan.slug}
                    isDisabled={Boolean(loadingPlan) || supplierClaimGate.decisionsBlocked}
                    onSelect={() => void handleSelectPlan(plan.slug)}
                  />
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-border/70 bg-muted/30 p-4 text-center text-sm text-muted-foreground">
                Choosing a plan opens secure Stripe Checkout in the same tab to complete payment and return to Supply Guard.
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2 text-center">
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              Already subscribed? Refresh status
            </Button>
            <UseAnotherAccountButton />
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/** ENT-6459 (D4): no gate without an account escape; the same local sign-out as onboarding. */
function UseAnotherAccountButton() {
  const [isSigningOut, setIsSigningOut] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="gap-2 text-muted-foreground"
      data-testid="paywall-use-another-account"
      disabled={isSigningOut}
      onClick={() => {
        setIsSigningOut(true);
        void signOutLocally('/auth');
      }}
    >
      <LogOut className="h-4 w-4" />
      Use another account
    </Button>
  );
}

function PlanCard({
  plan,
  isAnnual,
  isSelected,
  isDisabled,
  onSelect,
}: {
  plan: BillingPlanView;
  isAnnual: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: () => void;
}) {
  const price = getBillingPlanAmount(plan, isAnnual ? 'annual' : 'monthly');
  const monthlyEquivalent = isAnnual ? Math.round(price / 12) : price;

  return (
    <div
      className={`relative overflow-visible rounded-xl border-2 p-3 transition-all ${
        plan.slug === 'growth'
          ? 'border-primary shadow-md'
          : 'border-border hover:border-primary/50 hover:shadow-sm'
      }`}
    >
      {plan.slug === 'growth' && (
        <div
          data-testid="subscription-gate-popular-cutout"
          className="absolute -top-2.5 left-1/2 z-10 -translate-x-1/2"
        >
          <Badge className="bg-primary text-primary-foreground text-xs">
            <Sparkles className="mr-1 h-3 w-3" />
            Popular
          </Badge>
        </div>
      )}

      <div className="flex min-h-[18.5rem] flex-col space-y-2.5 pt-3">
        <div>
          <h3 className="text-base font-semibold lg:text-lg">{plan.name}</h3>
          <p className="text-xs text-muted-foreground lg:text-sm">Up to {plan.supplierLimit.toLocaleString()} suppliers</p>
        </div>

        <div>
          <span className="text-2xl font-bold lg:text-3xl">{formatPlanPrice(monthlyEquivalent)}</span>
          <span className="text-muted-foreground text-sm">/mo</span>
          {isAnnual ? (
            <p className="text-xs text-muted-foreground mt-1">
              {formatPlanPrice(price)} billed annually
            </p>
          ) : null}
        </div>

        <Separator />

        <ul className="space-y-1 text-sm">
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span>{plan.supplierLimit.toLocaleString()} suppliers</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span>Security scanning</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span>Certification tracking</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span>Questionnaire guidance</span>
          </li>
        </ul>

        <Button
          className="mt-auto h-11 w-full px-2 text-[11px] sm:text-xs"
          variant={plan.slug === 'growth' ? 'default' : 'outline'}
          onClick={onSelect}
          disabled={isDisabled}
        >
          {isSelected ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Opening checkout…
            </>
          ) : 'Select plan'}
        </Button>
        <p
          aria-live="polite"
          className={`text-center text-xs text-muted-foreground transition-opacity duration-200 ${
            isSelected ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {isSelected ? "You'll return here after payment." : <>&nbsp;</>}
        </p>
      </div>
    </div>
  );
}
